import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { NotebookPen, Plus, Search, X, LayoutGrid, List, Archive, AtSign, ArrowRightLeft, ExternalLink } from "lucide-react";
import { relatedLink } from "@/data/relatedRecords";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/common/StatusBadge";
import { api } from "@/lib/api";
import { useCrud } from "@/hooks/useCrud";
import { useUser } from "@/context/UserContext";
import { QuickCapture } from "./QuickCapture";
import { NoteCard } from "./NoteCard";
import { NoteEditorModal } from "./NoteEditorModal";
import { NoteDetail } from "./NoteDetail";
import { ConvertDialog } from "./ConvertDialog";
import { MentionsInbox } from "./MentionsInbox";
import { NOTE_SORTS, NOTE_CATEGORIES, NOTE_COLUMNS, colourStyle, catTone, statusBadgeTone } from "@/data/notebookOptions";
import { OWNERS } from "@/data/commandOptions";

const BOARD_VIEWS = [
  { value: "board", label: "Board", icon: LayoutGrid },
  { value: "list", label: "List", icon: List },
  { value: "archived", label: "Archived", icon: Archive },
];
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return "—"; } };

export const NotebookPanel = ({ openNoteId, initialView, onConsume, onMentionsChanged }) => {
  const { items, refetch, save } = useCrud("notes", "Note");
  const { currentUser, canWrite, isAdmin } = useUser();
  const [view, setView] = useState(() => (initialView === "mentions" || initialView === "alerts") ? "alerts" : "board");
  const [q, setQ] = useState("");
  const [fAuthor, setFAuthor] = useState("all"); const [fCat, setFCat] = useState("all"); const [fPinned, setFPinned] = useState("all");
  const [sort, setSort] = useState("pinned");
  const [editorOpen, setEditorOpen] = useState(false); const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [convertNote, setConvertNote] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const canEditNote = useCallback((n) => isAdmin || (canWrite && n.author === currentUser.name), [isAdmin, canWrite, currentUser]);

  const isFounder = currentUser.name === "Steven" || currentUser.name === "Richard";
  const [alertsCount, setAlertsCount] = useState(0);
  const [alertsKey, setAlertsKey] = useState(0);
  const loadAlertsCount = useCallback(() => { if (isFounder) api.mentionsUnreadCount().then((r) => setAlertsCount(r.count)).catch(() => {}); }, [isFounder]);

  const onMentionsChangedRef = useRef(onMentionsChanged);
  useEffect(() => { onMentionsChangedRef.current = onMentionsChanged; }, [onMentionsChanged]);
  const refreshMentions = useCallback(() => { onMentionsChangedRef.current?.(); loadAlertsCount(); setAlertsKey((k) => k + 1); }, [loadAlertsCount]);
  const refreshDetail = useCallback(async (id) => { try { setDetail(await api.get("notes", id)); } catch { setDetail(null); } }, []);
  useEffect(() => { loadAlertsCount(); }, [loadAlertsCount, items]);

  const openFromMention = useCallback(async (m, markRead = true) => {
    if (markRead && !m.read) { try { await api.mentionRead(m.id, true); } catch { /* noop */ } }
    try { setDetail(await api.get("notes", m.note_id)); } catch { toast.error("That note is unavailable — it may be archived or deleted."); }
    onMentionsChangedRef.current?.(); loadAlertsCount(); setAlertsKey((k) => k + 1);
  }, [loadAlertsCount]);

  // URL-driven note opening (runs once per distinct note id)
  const openedNoteRef = useRef(null);
  const onConsumeRef = useRef(onConsume);
  useEffect(() => { onConsumeRef.current = onConsume; }, [onConsume]);
  useEffect(() => {
    if (!openNoteId) { openedNoteRef.current = null; return; }
    if (openNoteId === openedNoteRef.current || !items.length) return;
    const n = items.find((x) => x.id === openNoteId);
    if (n) { openedNoteRef.current = openNoteId; setDetail(n); onConsumeRef.current?.(); }
  }, [openNoteId, items]);

  // shared filter + search (excludes archived unless the Archived view)
  const filtered = useMemo(() => {
    let rows = [...items];
    rows = view === "archived" ? rows.filter((n) => n.status === "Archived") : rows.filter((n) => n.status !== "Archived");
    if (q) { const s = q.toLowerCase(); rows = rows.filter((n) => n.title.toLowerCase().includes(s) || (n.body || "").toLowerCase().includes(s)); }
    if (fAuthor !== "all") rows = rows.filter((n) => n.author === fAuthor);
    if (fCat !== "all") rows = rows.filter((n) => n.category === fCat);
    if (fPinned === "pinned") rows = rows.filter((n) => n.pinned);
    return rows;
  }, [items, view, q, fAuthor, fCat, fPinned]);

  // pinned float to top within each column, then by chosen sort
  const sortNotes = useCallback((rows) => {
    const s = {
      pinned: (a, b) => (b.pinned - a.pinned) || (b.created_at || "").localeCompare(a.created_at || ""),
      newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      updated: (a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""),
      title: (a, b) => a.title.localeCompare(b.title),
    }[sort];
    return [...rows].sort((a, b) => (b.pinned - a.pinned) || s(a, b));
  }, [sort]);

  const byColumn = useMemo(() => {
    const map = Object.fromEntries(NOTE_COLUMNS.map((c) => [c.value, []]));
    filtered.forEach((n) => { const col = map[n.column] ? n.column : "Shared"; map[col].push(n); });
    Object.keys(map).forEach((k) => { map[k] = sortNotes(map[k]); });
    return map;
  }, [filtered, sortNotes]);

  const openAdd = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (n) => { setDetail(null); setEditing(n); setEditorOpen(true); };
  const togglePin = async (n) => { await api.notePin(n.id, !n.pinned); toast.success(n.pinned ? "Unpinned" : "Pinned"); await refetch(); if (detail?.id === n.id) refreshDetail(n.id); };
  const toggleArchive = async (n) => { await api.noteArchive(n.id, n.status !== "Archived"); toast.success(n.status === "Archived" ? "Restored" : "Archived"); await refetch(); if (detail?.id === n.id) refreshDetail(n.id); };
  const addComment = async (id, text, mentions) => { await api.noteComment(id, text, mentions); await refetch(); refreshMentions(); await refreshDetail(id); };
  const doDelete = async (reason) => { await api.remove("notes", confirmId, reason); toast.success("Moved to Recycle Bin"); setConfirmId(null); setDetail(null); await refetch(); };
  const handleSave = async (values) => { await save(values); await refetch(); refreshMentions(); };
  const doConvert = async (target, data) => { const res = await api.noteConvert(convertNote.id, target, data); toast.success(`Created ${target}`); await refetch(); await refreshDetail(convertNote.id); return res; };
  const doReact = async (note, emoji) => { try { await api.noteReact(note.id, emoji); await refetch(); if (detail?.id === note.id) refreshDetail(note.id); } catch (err) { toast.error(err?.response?.data?.detail || "Could not react"); } };

  const onDragStart = (e, note) => { e.dataTransfer.setData("text/plain", note.id); e.dataTransfer.effectAllowed = "move"; };
  const onDrop = async (e, col) => {
    e.preventDefault(); setDragOverCol(null);
    const id = e.dataTransfer.getData("text/plain");
    const note = items.find((n) => n.id === id);
    if (!note || !canEditNote(note) || note.column === col) return;
    try { await api.noteMove(id, col); await refetch(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not move note"); }
  };

  const resetFilters = () => { setQ(""); setFAuthor("all"); setFCat("all"); setFPinned("all"); };
  const activeFilters = [fAuthor, fCat, fPinned].filter((x) => x !== "all").length + (q ? 1 : 0);

  return (
    <div data-testid="notebook-panel" className="space-y-5">
      {canWrite && (view === "board" || view === "list") && <QuickCapture onSave={handleSave} />}

      <SectionCard testid="notebook-card" accent="#E0A82E" icon={NotebookPen} eyebrow="Shared thinking space" title="Founders Notebook"
        subtitle="A post-it board for ideas, reminders, questions, wins and things to revisit — captured loosely, not as formal tasks."
        action={canWrite ? <Button size="sm" data-testid="add-note-btn" onClick={openAdd} className="rounded-full bg-[#0D1B2A] text-white hover:bg-[#1B2A3B]"><Plus size={15} /> New Note</Button> : undefined}>

        {/* View toggle */}
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          {[...BOARD_VIEWS, ...(isFounder ? [{ value: "alerts", label: "Alerts", icon: AtSign, badge: alertsCount }] : [])].map((v) => (
            <button key={v.value} data-testid={`notebook-view-${v.value}`} onClick={() => setView(v.value)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${view === v.value ? "bg-[#E0A82E] text-white shadow-sm" : "bg-[#F5F1E6] text-[#8A6314] hover:bg-[#EDE6D3]"}`}>
              <v.icon size={14} /> {v.label}
              {v.badge > 0 && <span data-testid="notebook-alerts-count" className={`ml-0.5 inline-flex min-w-[1.1rem] items-center justify-center rounded-full px-1 text-[10px] font-bold leading-4 ${view === v.value ? "bg-white/25 text-white" : "bg-[#E0A82E] text-white"}`}>{v.badge}</span>}
            </button>
          ))}
        </div>

        {/* Search + filters */}
        <div className="mb-4 flex flex-col gap-2.5">
          <div className="relative"><Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8674]" /><Input data-testid="notebook-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes…" className="border-[#E7E2D4] bg-white pl-9" /></div>
          <div className="flex flex-wrap gap-2">
            <F testid="notebook-filter-author" v={fAuthor} on={setFAuthor} label="Author" opts={OWNERS} />
            <F testid="notebook-filter-category" v={fCat} on={setFCat} label="Category" opts={NOTE_CATEGORIES} />
            <F testid="notebook-filter-pinned" v={fPinned} on={setFPinned} label="Pinned" opts={[{ value: "pinned", label: "Pinned only" }]} />
            <Select value={sort} onValueChange={setSort}><SelectTrigger data-testid="notebook-sort" className="w-auto min-w-[140px] border-[#E7E2D4] bg-white"><SelectValue /></SelectTrigger><SelectContent>{NOTE_SORTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
            {activeFilters > 0 && <button data-testid="notebook-clear-filters" onClick={resetFilters} className="inline-flex items-center gap-1 rounded-full bg-[#F5F1E6] px-3 py-1.5 text-sm font-semibold text-[#8A6314] hover:bg-[#EDE6D3]"><X size={14} /> Clear ({activeFilters})</button>}
          </div>
        </div>

        {items.length === 0 ? (
          <EmptyState icon={NotebookPen} image="/commando-mascot-v2.webp" title="Your notebook is empty"
            description="Capture your first thought with the Quick Note box above, or hit New Note."
            actionLabel={canWrite ? "New note" : undefined} onAction={canWrite ? openAdd : undefined} />
        ) : view === "board" ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6" data-testid="notebook-board">
            {NOTE_COLUMNS.map((col) => {
              const notes = byColumn[col.value] || [];
              const over = dragOverCol === col.value;
              return (
                <div key={col.value} data-testid={`notebook-col-${col.value.replace(/\s/g, "-").toLowerCase()}`}
                  onDragOver={(e) => { if (canWrite) { e.preventDefault(); setDragOverCol(col.value); } }}
                  onDragLeave={() => setDragOverCol((c) => (c === col.value ? null : c))}
                  onDrop={(e) => onDrop(e, col.value)}
                  className={`flex flex-col rounded-2xl border p-2.5 transition-colors ${over ? "border-[#E0A82E] bg-[#FBF3DC]" : "border-[#E7E2D4] bg-[#FAF7EF]/70"}`}>
                  <div className="mb-2 flex items-center justify-between px-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#8A6314]">{col.label}</span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-[#8A8674]">{notes.length}</span>
                  </div>
                  <div className="flex flex-col gap-3">
                    {notes.length === 0
                      ? <p className="px-1 py-6 text-center text-xs text-[#B7B09A]">Drop a note here</p>
                      : notes.map((n, i) => <NoteCard key={n.id} note={n} index={i} canWrite={canWrite} myName={currentUser.name} onOpen={setDetail} onTogglePin={togglePin} onReact={doReact} onDragStart={onDragStart} />)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : view === "list" ? (
          <ListView rows={sortNotes(filtered)} onOpen={setDetail} />
        ) : view === "alerts" ? (
          <MentionsInbox refreshKey={alertsKey} onOpen={openFromMention} onChange={() => { onMentionsChangedRef.current?.(); loadAlertsCount(); }} />
        ) : (
          filtered.length === 0
            ? <EmptyState icon={Archive} title="No archived notes" description="Notes you archive will appear here." />
            : <ListView rows={sortNotes(filtered)} onOpen={setDetail} />
        )}
      </SectionCard>

      <NoteEditorModal open={editorOpen} onOpenChange={setEditorOpen} initial={editing} onSubmit={handleSave} />
      <NoteDetail note={detail} open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}
        canWrite={canWrite} canEditThis={detail ? canEditNote(detail) : false} myName={currentUser.name}
        onEdit={openEdit} onDelete={(n) => setConfirmId(n.id)} onPin={togglePin} onArchive={toggleArchive}
        onConvert={(n) => setConvertNote(n)} onComment={addComment} onReact={doReact} />
      <ConvertDialog open={!!convertNote} onOpenChange={(o) => !o && setConvertNote(null)} note={convertNote} onConverted={doConvert} />
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)} title="Delete this note?" description="It will be moved to the Recycle Bin." onConfirm={doDelete} softDelete />
    </div>
  );
};

const ListView = ({ rows, onOpen }) => (
  <div className="overflow-x-auto rounded-2xl border border-[#E7E2D4]" data-testid="notebook-list">
    <table className="w-full min-w-[720px] text-left text-sm">
      <thead className="bg-[#F5F1E6] text-[11px] font-bold uppercase tracking-wider text-[#8A6314]">
        <tr>
          <th className="px-3 py-2">Title</th><th className="px-3 py-2">Category</th><th className="px-3 py-2">Author</th>
          <th className="px-3 py-2">Column</th><th className="px-3 py-2">Colour</th><th className="px-3 py-2">Pinned</th>
          <th className="px-3 py-2">Updated</th><th className="px-3 py-2">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((n) => {
          const cs = colourStyle(n.colour);
          return (
            <tr key={n.id} data-testid={`note-row-${n.id}`} onClick={() => onOpen(n)} className="cursor-pointer border-t border-[#EFEADB] hover:bg-[#FBF8F0]">
              <td className="px-3 py-2 font-semibold text-[#0D1B2A]">{n.title}</td>
              <td className="px-3 py-2"><span className="font-semibold" style={{ color: catTone(n.category) }}>{n.category}</span></td>
              <td className="px-3 py-2 text-[#5C6B7B]">{n.author}</td>
              <td className="px-3 py-2 text-[#5C6B7B]">{n.column}</td>
              <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5 text-[#5C6B7B]"><span className="h-3 w-3 rounded-full" style={{ backgroundColor: cs.dot }} /> {n.colour}</span></td>
              <td className="px-3 py-2 text-[#5C6B7B]">{n.pinned ? "Yes" : "—"}</td>
              <td className="px-3 py-2 text-[#8A8674]">{new Date(n.updated_at || n.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</td>
              <td className="px-3 py-2">
                <StatusBadge status={statusBadgeTone(n.status)} label={n.status} />
                {(n.converted_to?.length || 0) > 0 && (() => {
                  const c = n.converted_to[n.converted_to.length - 1];
                  return (
                    <div className="mt-1">
                      {c.record_id ? (
                        <Link to={relatedLink({ type: c.target, id: c.record_id, name: c.record_name })} data-testid={`note-row-converted-${n.id}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1D4ED8] hover:underline"><ArrowRightLeft size={10} /> Converted to {c.target} <ExternalLink size={10} /></Link>
                      ) : (
                        <span data-testid={`note-row-converted-${n.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8A6314]"><ArrowRightLeft size={10} /> Converted to {c.target}</span>
                      )}
                    </div>
                  );
                })()}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const F = ({ testid, v, on, label, opts }) => (
  <Select value={v} onValueChange={on}><SelectTrigger data-testid={testid} className="w-auto min-w-[120px] border-[#E7E2D4] bg-white"><SelectValue placeholder={label} /></SelectTrigger><SelectContent><SelectItem value="all">{label}: All</SelectItem>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
);
